import React, { useState } from 'react';
import Layout from '@/components/layout/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, Play, CheckCircle, XCircle, Server, Search, Code, Zap } from 'lucide-react';
import { toast } from 'sonner';

import { getEdgeFunctionUrl } from '@/utils/supabaseUrl';

const MCP_SERVER_URL = getEdgeFunctionUrl('mcp-server');

interface TestResult {
  status: 'success' | 'error' | 'loading';
  data?: any;
  error?: string;
  timestamp: number;
}

const MCPServerTest: React.FC = () => {
  const [productQuery, setProductQuery] = useState('watermelon');
  const [customRequest, setCustomRequest] = useState(JSON.stringify({
    "jsonrpc": "2.0",
    "method": "tools/call",
    "params": {
      "name": "price_lookup",
      "arguments": {
        "product": "watermelon"
      }
    },
    "id": 1
  }, null, 2));
  
  const [results, setResults] = useState<{
    listTools: TestResult | null;
    priceLookup: TestResult | null;
    custom: TestResult | null;
  }>({
    listTools: null,
    priceLookup: null,
    custom: null
  });

  const makeRequest = async (body: any) => {
    const response = await fetch(MCP_SERVER_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body)
    });
    
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    
    return await response.json();
  };

  const testListTools = async () => {
    setResults(prev => ({ ...prev, listTools: { status: 'loading', timestamp: Date.now() } }));
    
    try {
      const request = {
        jsonrpc: "2.0",
        method: "tools/list",
        params: {},
        id: 1
      };
      
      const result = await makeRequest(request);
      setResults(prev => ({ 
        ...prev, 
        listTools: { status: 'success', data: result, timestamp: Date.now() } 
      }));
      toast.success('Tools list retrieved successfully');
    } catch (error: any) {
      setResults(prev => ({ 
        ...prev, 
        listTools: { status: 'error', error: error.message, timestamp: Date.now() } 
      }));
      toast.error('Failed to list tools');
    }
  };

  const testPriceLookup = async () => {
    setResults(prev => ({ ...prev, priceLookup: { status: 'loading', timestamp: Date.now() } }));
    
    try {
      const request = {
        jsonrpc: "2.0",
        method: "tools/call",
        params: {
          name: "price_lookup",
          arguments: {
            product: productQuery
          }
        },
        id: 1
      };
      
      const result = await makeRequest(request);
      setResults(prev => ({ 
        ...prev, 
        priceLookup: { status: 'success', data: result, timestamp: Date.now() } 
      }));
      toast.success(`Price lookup for "${productQuery}" completed`);
    } catch (error: any) {
      setResults(prev => ({ 
        ...prev, 
        priceLookup: { status: 'error', error: error.message, timestamp: Date.now() } 
      }));
      toast.error('Price lookup failed');
    }
  };

  const testCustomRequest = async () => {
    setResults(prev => ({ ...prev, custom: { status: 'loading', timestamp: Date.now() } }));
    
    try {
      const request = JSON.parse(customRequest);
      const result = await makeRequest(request);
      setResults(prev => ({ 
        ...prev, 
        custom: { status: 'success', data: result, timestamp: Date.now() } 
      }));
      toast.success('Custom request completed');
    } catch (error: any) {
      setResults(prev => ({ 
        ...prev, 
        custom: { status: 'error', error: error.message, timestamp: Date.now() } 
      }));
      toast.error('Custom request failed');
    }
  };

  const renderResult = (result: TestResult | null) => {
    if (!result) return null;

    const getStatusIcon = () => {
      switch (result.status) {
        case 'loading':
          return <Loader2 className="h-4 w-4 animate-spin" />;
        case 'success':
          return <CheckCircle className="h-4 w-4 text-green-600" />;
        case 'error':
          return <XCircle className="h-4 w-4 text-red-600" />;
      }
    };

    const getStatusColor = () => {
      switch (result.status) {
        case 'loading':
          return 'bg-blue-50 border-blue-200';
        case 'success':
          return 'bg-green-50 border-green-200';
        case 'error':
          return 'bg-red-50 border-red-200';
      }
    };

    return (
      <div className={`mt-4 p-4 border rounded-lg ${getStatusColor()}`}>
        <div className="flex items-center gap-2 mb-2">
          {getStatusIcon()}
          <span className="text-sm font-medium">
            {result.status === 'loading' ? 'Testing...' : 
             result.status === 'success' ? 'Success' : 'Error'}
          </span>
          <Badge variant="outline" className="ml-auto text-xs">
            {new Date(result.timestamp).toLocaleTimeString()}
          </Badge>
        </div>
        
        {result.error && (
          <Alert className="mb-2">
            <AlertDescription className="text-red-700">
              {result.error}
            </AlertDescription>
          </Alert>
        )}
        
        {result.data && (
          <div className="bg-gray-900 text-green-400 p-3 rounded font-mono text-xs overflow-auto max-h-64">
            <pre>{JSON.stringify(result.data, null, 2)}</pre>
          </div>
        )}
      </div>
    );
  };

  return (
    <Layout>
      <div className="container mx-auto py-6">
        <div className="flex items-center gap-3 mb-6">
          <Server className="h-8 w-8 text-primary" />
          <div>
            <h1 className="text-3xl font-bold">MCP Server Test Interface</h1>
            <p className="text-muted-foreground">
              Test and debug your Meta-Communication Protocol server
            </p>
          </div>
        </div>

        <div className="grid gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Zap className="h-5 w-5" />
                Server Status
              </CardTitle>
              <CardDescription>
                MCP Server endpoint: <code className="bg-muted px-1 rounded">{MCP_SERVER_URL}</code>
              </CardDescription>
            </CardHeader>
          </Card>

          <Tabs defaultValue="tools" className="w-full">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="tools" className="flex items-center gap-2">
                <Code className="h-4 w-4" />
                List Tools
              </TabsTrigger>
              <TabsTrigger value="price" className="flex items-center gap-2">
                <Search className="h-4 w-4" />
                Price Lookup
              </TabsTrigger>
              <TabsTrigger value="custom" className="flex items-center gap-2">
                <Play className="h-4 w-4" />
                Custom Request
              </TabsTrigger>
            </TabsList>

            <TabsContent value="tools" className="mt-6">
              <Card>
                <CardHeader>
                  <CardTitle>Available Tools</CardTitle>
                  <CardDescription>
                    List all available tools from the MCP server
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Button 
                    onClick={testListTools} 
                    disabled={results.listTools?.status === 'loading'}
                    className="w-full"
                  >
                    {results.listTools?.status === 'loading' ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <Code className="h-4 w-4 mr-2" />
                    )}
                    List Available Tools
                  </Button>
                  {renderResult(results.listTools)}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="price" className="mt-6">
              <Card>
                <CardHeader>
                  <CardTitle>Price Lookup Tool</CardTitle>
                  <CardDescription>
                    Test the price lookup functionality with specific products
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm font-medium mb-2 block">Product Name</label>
                      <Input
                        value={productQuery}
                        onChange={(e) => setProductQuery(e.target.value)}
                        placeholder="Enter product name (e.g., watermelon, tomato)"
                        className="w-full"
                      />
                    </div>
                    <Button 
                      onClick={testPriceLookup} 
                      disabled={results.priceLookup?.status === 'loading'}
                      className="w-full"
                    >
                      {results.priceLookup?.status === 'loading' ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      ) : (
                        <Search className="h-4 w-4 mr-2" />
                      )}
                      Test Price Lookup
                    </Button>
                  </div>
                  {renderResult(results.priceLookup)}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="custom" className="mt-6">
              <Card>
                <CardHeader>
                  <CardTitle>Custom JSON-RPC Request</CardTitle>
                  <CardDescription>
                    Send custom requests to test advanced functionality
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm font-medium mb-2 block">JSON-RPC Request</label>
                      <Textarea
                        value={customRequest}
                        onChange={(e) => setCustomRequest(e.target.value)}
                        placeholder="Enter custom JSON-RPC request"
                        className="font-mono text-xs min-h-32"
                      />
                    </div>
                    <Button 
                      onClick={testCustomRequest} 
                      disabled={results.custom?.status === 'loading'}
                      className="w-full"
                    >
                      {results.custom?.status === 'loading' ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      ) : (
                        <Play className="h-4 w-4 mr-2" />
                      )}
                      Send Custom Request
                    </Button>
                  </div>
                  {renderResult(results.custom)}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </Layout>
  );
};

export default MCPServerTest;